import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { Navbar } from "@/components/Navbar";
import { DPRView } from "@/components/DPRView";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft,
  IndianRupee,
  Lock,
  CheckCircle,
  Sparkles,
  Loader2,
  Share2,
  MessageCircle,
  TrendingUp,
  MapPin,
  Zap,
  AlertTriangle,
  Target,
  BarChart3,
  FileText,
  ArrowUpRight,
  Factory,
  Users,
  Award,
  Building2,
  Briefcase,
  ShoppingCart,
  Smartphone,
  Landmark,
  Rocket,
  Package,
  Globe,
  BadgeCheck,
  Lightbulb,
  PiggyBank,
  Store,
  Truck,
  Scale,
  ShieldCheck,
  GraduationCap,
  Download,
  Phone,
  Mail,
  ExternalLink,
  Star
} from "lucide-react";

// Category-wise images/icons mapping
const getCategoryIcon = (category) => {
  const cat = category?.toUpperCase() || '';
  if (cat.includes('AGRO')) return '🌾';
  if (cat.includes('SERVICE')) return '🛠️';
  if (cat.includes('CHEMICAL') || cat.includes('POLYMER')) return '🧪';
  if (cat.includes('MINERAL')) return '⛏️';
  if (cat.includes('FOREST')) return '🌲';
  if (cat.includes('RURAL') || cat.includes('ENGINEERING')) return '🔧';
  if (cat.includes('TEXTILE')) return '🧵';
  return '🏭';
};

// Simple Growth Chart Component
const GrowthChart = ({ year1 = 100, year2 = 140, year3 = 180 }) => {
  const maxValue = Math.max(year1, year2, year3);
  const getHeight = (value) => `${(value / maxValue) * 100}%`;
  
  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 border border-gray-300 shadow-xl">
      <h4 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-3">
        <div className="p-2 bg-orange-500/20 rounded-lg">
          <BarChart3 className="w-5 h-5 text-orange-500" />
        </div>
        3 साल में अनुमानित ग्रोथ
      </h4>
      <div className="flex items-end justify-around h-40 gap-6 px-4">
        {/* Year 1 */}
        <div className="flex flex-col items-center gap-3 flex-1">
          <div className="w-full max-w-20 bg-slate-700/50 rounded-xl relative" style={{ height: '100%' }}>
            <div 
              className="absolute bottom-0 w-full bg-gradient-to-t from-orange-600 via-orange-500 to-orange-400 rounded-xl transition-all duration-1000 shadow-lg shadow-orange-500/30"
              style={{ height: getHeight(year1) }}
            >
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-sm font-bold text-orange-400 bg-white/80 px-2 py-1 rounded">
                {year1}%
              </span>
            </div>
          </div>
          <span className="text-sm font-medium text-gray-600">साल 1</span>
        </div>
        
        {/* Year 2 */}
        <div className="flex flex-col items-center gap-3 flex-1">
          <div className="w-full max-w-20 bg-slate-700/50 rounded-xl relative" style={{ height: '100%' }}>
            <div 
              className="absolute bottom-0 w-full bg-gradient-to-t from-green-600 via-green-500 to-green-400 rounded-xl transition-all duration-1000 delay-300 shadow-lg shadow-green-500/30"
              style={{ height: getHeight(year2) }}
            >
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-sm font-bold text-green-400 bg-white/80 px-2 py-1 rounded">
                {year2}%
              </span>
            </div>
          </div>
          <span className="text-sm font-medium text-gray-600">साल 2</span>
        </div>
        
        {/* Year 3 */}
        <div className="flex flex-col items-center gap-3 flex-1">
          <div className="w-full max-w-20 bg-slate-700/50 rounded-xl relative" style={{ height: '100%' }}>
            <div 
              className="absolute bottom-0 w-full bg-gradient-to-t from-blue-600 via-blue-500 to-blue-400 rounded-xl transition-all duration-1000 delay-500 shadow-lg shadow-blue-500/30"
              style={{ height: getHeight(year3) }}
            >
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 text-sm font-bold text-blue-400 bg-white/80 px-2 py-1 rounded">
                {year3}%
              </span>
            </div>
          </div>
          <span className="text-sm font-medium text-gray-600">साल 3</span>
        </div>
      </div>
      <p className="text-xs text-gray-8000 mt-6 text-center">* बेस इयर (100%) से तुलना</p>
    </div>
  );
};

// Introduction Section with Image
const IntroductionSection = ({ project, highlights }) => {
  const categoryEmoji = getCategoryIcon(project?.category);
  
  // Parse introduction into bullet points
  const parseIntroduction = (text) => {
    if (!text) return [];
    const paragraphs = text.split('\n\n').filter(p => p.trim());
    return paragraphs;
  };
  
  const introParagraphs = parseIntroduction(highlights?.introduction_hindi);
  
  return (
    <div className="bg-gradient-to-br from-white via-gray-50 to-slate-900 rounded-2xl border border-gray-300 overflow-hidden shadow-2xl">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-orange-600/20 via-orange-500/10 to-transparent p-6 border-b border-gray-300/50">
        <h3 className="font-rajdhani font-bold text-2xl text-gray-800 flex items-center gap-3">
          <div className="p-3 bg-orange-500/20 rounded-xl border border-orange-500/30">
            <FileText className="w-6 h-6 text-orange-500" />
          </div>
          परिचय - इस व्यवसाय के बारे में
        </h3>
      </div>
      
      <div className="p-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Left - Visual/Image Section */}
          <div className="md:col-span-1">
            <div className="bg-gradient-to-br from-orange-500/10 via-gray-50 to-slate-900 rounded-2xl p-6 border border-orange-500/20 h-full flex flex-col items-center justify-center text-center">
              {/* Large Category Emoji */}
              <div className="text-7xl mb-4">{categoryEmoji}</div>
              <h4 className="font-rajdhani font-bold text-xl text-orange-400 mb-2">
                {project?.name_hindi}
              </h4>
              <p className="text-gray-500 text-sm mb-4">{project?.name}</p>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-3 w-full mt-4">
                <div className="bg-gray-100/80 rounded-lg p-3 border border-gray-300">
                  <Factory className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">श्रेणी</p>
                  <p className="text-xs font-semibold text-gray-700 truncate">{project?.category}</p>
                </div>
                <div className="bg-gray-100/80 rounded-lg p-3 border border-gray-300">
                  <IndianRupee className="w-5 h-5 text-green-400 mx-auto mb-1" />
                  <p className="text-xs text-gray-500">निवेश</p>
                  <p className="text-xs font-semibold text-green-400">₹{(project?.total_cost / 100000).toFixed(1)}L</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Right - Text Content */}
          <div className="md:col-span-2 space-y-4">
            {introParagraphs.length > 0 ? (
              introParagraphs.map((para, idx) => (
                <div key={idx} className="flex gap-4">
                  <div className="shrink-0 mt-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold text-sm
                      ${idx === 0 ? 'bg-gradient-to-br from-orange-500 to-orange-600' : 
                        idx === 1 ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                        idx === 2 ? 'bg-gradient-to-br from-green-500 to-green-600' :
                        idx === 3 ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                        'bg-gradient-to-br from-slate-500 to-slate-600'}`}
                    >
                      {idx + 1}
                    </div>
                  </div>
                  <p className="text-gray-600 leading-relaxed text-sm">{para}</p>
                </div>
              ))
            ) : (
              <p className="text-gray-600 leading-relaxed">
                {project?.name_hindi} का व्यवसाय शुरू करें। यह एक लाभदायक व्यवसाय है जो ₹{project?.total_cost?.toLocaleString('hi-IN')} के निवेश में अच्छा मुनाफा दे सकता है।
              </p>
            )}
            
            {/* Key Highlights Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 pt-4 border-t border-gray-300/50">
              <div className="bg-gray-100/50 rounded-xl p-3 border border-gray-300 text-center">
                <Users className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                <p className="text-xs text-gray-500">कर्मचारी</p>
                <p className="text-sm font-bold text-gray-700">3-8 लोग</p>
              </div>
              <div className="bg-gray-100/50 rounded-xl p-3 border border-gray-300 text-center">
                <MapPin className="w-5 h-5 text-green-400 mx-auto mb-2" />
                <p className="text-xs text-gray-500">जगह</p>
                <p className="text-sm font-bold text-gray-700">{highlights?.area_required_hindi || '500 वर्ग फीट'}</p>
              </div>
              <div className="bg-gray-100/50 rounded-xl p-3 border border-gray-300 text-center">
                <Award className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
                <p className="text-xs text-gray-500">लाइसेंस</p>
                <p className="text-sm font-bold text-gray-700">MSME, GST</p>
              </div>
              <div className="bg-gray-100/50 rounded-xl p-3 border border-gray-300 text-center">
                <Landmark className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                <p className="text-xs text-gray-500">सब्सिडी</p>
                <p className="text-sm font-bold text-gray-700">PMEGP पात्र</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Market Potential Card Component
const MarketCard = ({ icon: Icon, emoji, title, color, items, gradientFrom, gradientTo }) => {
  return (
    <div className={`bg-gradient-to-br ${gradientFrom} ${gradientTo} rounded-2xl border border-gray-300 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1`}>
      {/* Card Header */}
      <div className={`p-4 border-b border-gray-300/50 bg-gradient-to-r from-${color}-500/20 to-transparent`}>
        <div className="flex items-center gap-3">
          <div className={`p-3 bg-${color}-500/20 rounded-xl border border-${color}-500/30`}>
            {emoji ? (
              <span className="text-2xl">{emoji}</span>
            ) : (
              <Icon className={`w-6 h-6 text-${color}-400`} />
            )}
          </div>
          <h4 className={`font-bold text-lg text-${color}-400`}>{title}</h4>
        </div>
      </div>
      
      {/* Card Content */}
      <div className="p-4 space-y-3">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-start gap-3 group">
            <div className={`w-6 h-6 rounded-full bg-${color}-500/20 flex items-center justify-center shrink-0 mt-0.5`}>
              <CheckCircle className={`w-3.5 h-3.5 text-${color}-400`} />
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">{item}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Why Business Will Succeed Section
const MarketPotentialSection = ({ highlights }) => {
  // Parse market potential into sections
  const parseMarketPotential = (text) => {
    if (!text) return {
      demand: ['स्थानीय बाजार में बढ़ती मांग', 'शहरी और ग्रामीण दोनों क्षेत्रों में अवसर'],
      revenue: ['B2B थोक बिक्री', 'B2C रिटेल बिक्री', 'ऑनलाइन मार्केटप्लेस'],
      growth: ['ब्रांड बनाकर विस्तार', 'नए प्रोडक्ट लाइन', 'फ्रेंचाइजी मॉडल'],
      government: ['PMEGP से 25-35% सब्सिडी', 'MUDRA लोन उपलब्ध', 'राज्य सरकार की योजनाएं']
    };
    
    const sections = {
      demand: [],
      revenue: [],
      growth: [],
      government: []
    };
    
    let currentSection = null;
    const lines = text.split('\n');
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (!trimmedLine) continue;
      
      if (trimmedLine.includes('बाजार की मांग') || trimmedLine.includes('मांग:')) {
        currentSection = 'demand';
        continue;
      } else if (trimmedLine.includes('आय के स्रोत') || trimmedLine.includes('आय:')) {
        currentSection = 'revenue';
        continue;
      } else if (trimmedLine.includes('ग्रोथ के अवसर') || trimmedLine.includes('ग्रोथ:') || trimmedLine.includes('अवसर:')) {
        currentSection = 'growth';
        continue;
      } else if (trimmedLine.includes('सरकारी सहायता') || trimmedLine.includes('सरकारी:')) {
        currentSection = 'government';
        continue;
      }
      
      if (currentSection && (trimmedLine.startsWith('-') || trimmedLine.startsWith('•') || trimmedLine.startsWith('*'))) {
        const cleanLine = trimmedLine.replace(/^[-•*]\s*/, '').replace(/\*\*/g, '');
        if (cleanLine.length > 10) {
          sections[currentSection].push(cleanLine);
        }
      }
    }
    
    // Ensure each section has at least some content
    if (sections.demand.length === 0) sections.demand = ['स्थानीय बाजार में बढ़ती मांग', 'टियर 2/3 शहरों में अवसर'];
    if (sections.revenue.length === 0) sections.revenue = ['B2B थोक बिक्री', 'रिटेल बिक्री', 'ऑनलाइन मार्केटप्लेस'];
    if (sections.growth.length === 0) sections.growth = ['ब्रांड बनाकर विस्तार', 'नए बाजारों में प्रवेश'];
    if (sections.government.length === 0) sections.government = ['PMEGP सब्सिडी', 'MUDRA लोन'];
    
    return sections;
  };
  
  const marketData = parseMarketPotential(highlights?.market_potential_hindi);
  
  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="bg-gradient-to-r from-orange-600/20 via-white to-slate-900 rounded-2xl p-6 border border-orange-500/30">
        <h3 className="font-rajdhani font-bold text-2xl text-gray-800 flex items-center gap-3">
          <div className="p-3 bg-orange-500/20 rounded-xl border border-orange-500/30">
            <Target className="w-6 h-6 text-orange-500" />
          </div>
          यह बिज़नेस क्यों चलेगा?
        </h3>
        <p className="text-gray-500 mt-2 ml-14">जानिए इस व्यवसाय की सफलता के कारण</p>
      </div>
      
      {/* Cards Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Market Demand Card */}
        <div className="bg-gradient-to-br from-white via-gray-50 to-slate-900 rounded-2xl border border-green-500/30 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
          <div className="p-5 border-b border-gray-300/50 bg-gradient-to-r from-green-500/20 to-transparent">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/30">
                <span className="text-3xl">🌾</span>
              </div>
              <div>
                <h4 className="font-bold text-xl text-green-400">बाजार की मांग</h4>
                <p className="text-xs text-gray-500">Market Demand</p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {marketData.demand.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center shrink-0 mt-0.5">
                  <CheckCircle className="w-3.5 h-3.5 text-green-400" />
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Revenue Sources Card */}
        <div className="bg-gradient-to-br from-white via-gray-50 to-slate-900 rounded-2xl border border-blue-500/30 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
          <div className="p-5 border-b border-gray-300/50 bg-gradient-to-r from-blue-500/20 to-transparent">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
                <span className="text-3xl">💰</span>
              </div>
              <div>
                <h4 className="font-bold text-xl text-blue-400">आय के स्रोत</h4>
                <p className="text-xs text-gray-500">Revenue Sources</p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {marketData.revenue.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center shrink-0 mt-0.5">
                  {idx === 0 ? <Store className="w-3.5 h-3.5 text-blue-400" /> :
                   idx === 1 ? <ShoppingCart className="w-3.5 h-3.5 text-blue-400" /> :
                   idx === 2 ? <Smartphone className="w-3.5 h-3.5 text-blue-400" /> :
                   <Truck className="w-3.5 h-3.5 text-blue-400" />}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Growth Opportunities Card */}
        <div className="bg-gradient-to-br from-white via-gray-50 to-slate-900 rounded-2xl border border-purple-500/30 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
          <div className="p-5 border-b border-gray-300/50 bg-gradient-to-r from-purple-500/20 to-transparent">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-500/20 rounded-xl border border-purple-500/30">
                <span className="text-3xl">📈</span>
              </div>
              <div>
                <h4 className="font-bold text-xl text-purple-400">ग्रोथ के अवसर</h4>
                <p className="text-xs text-gray-500">Growth Opportunities</p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {marketData.growth.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center shrink-0 mt-0.5">
                  {idx === 0 ? <Rocket className="w-3.5 h-3.5 text-purple-400" /> :
                   idx === 1 ? <Globe className="w-3.5 h-3.5 text-purple-400" /> :
                   idx === 2 ? <Package className="w-3.5 h-3.5 text-purple-400" /> :
                   <TrendingUp className="w-3.5 h-3.5 text-purple-400" />}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
        
        {/* Government Support Card */}
        <div className="bg-gradient-to-br from-white via-gray-50 to-slate-900 rounded-2xl border border-yellow-500/30 overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1">
          <div className="p-5 border-b border-gray-300/50 bg-gradient-to-r from-yellow-500/20 to-transparent">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-500/20 rounded-xl border border-yellow-500/30">
                <span className="text-3xl">🏛️</span>
              </div>
              <div>
                <h4 className="font-bold text-xl text-yellow-400">सरकारी सहायता</h4>
                <p className="text-xs text-gray-500">Government Support</p>
              </div>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {marketData.government.slice(0, 4).map((item, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center shrink-0 mt-0.5">
                  {idx === 0 ? <BadgeCheck className="w-3.5 h-3.5 text-yellow-400" /> :
                   idx === 1 ? <PiggyBank className="w-3.5 h-3.5 text-yellow-400" /> :
                   idx === 2 ? <Scale className="w-3.5 h-3.5 text-yellow-400" /> :
                   <ShieldCheck className="w-3.5 h-3.5 text-yellow-400" />}
                </div>
                <p className="text-sm text-gray-600 leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const getRiskColor = (risk) => {
  const r = risk?.toLowerCase() || '';
  if (r.includes('low') || r.includes('कम')) return 'text-green-500 bg-green-500/10 border-green-500/30';
  if (r.includes('high') || r.includes('उच्च')) return 'text-red-500 bg-red-500/10 border-red-500/30';
  return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/30';
};

export default function ProjectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [project, setProject] = useState(null);
  const [highlights, setHighlights] = useState(null);
  const [dpr, setDpr] = useState(null);
  const [relevantSuppliers, setRelevantSuppliers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [highlightsLoading, setHighlightsLoading] = useState(false);
  const [dprLoading, setDprLoading] = useState(false);
  const [suppliersLoading, setSuppliersLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [isPurchased, setIsPurchased] = useState(false);
  const [showDPR, setShowDPR] = useState(false);

  const fetchProject = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/projects/${id}`);
      setProject(response.data);
      if (response.data.highlights) {
        setHighlights(response.data.highlights);
      }
    } catch (error) {
      toast.error("प्रोजेक्ट नहीं मिला");
      navigate('/');
    }
    setLoading(false);
  }, [id, navigate]);

  const fetchHighlights = useCallback(async () => {
    if (highlights) return;
    setHighlightsLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${id}/highlights`);
      setHighlights(response.data);
    } catch (error) {
      console.error('Failed to fetch highlights:', error);
    }
    setHighlightsLoading(false);
  }, [id, highlights]);

  const checkPurchase = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/user/purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const purchased = response.data.some(p => p.id === id);
      setIsPurchased(purchased);
    } catch (error) {
      console.error('Failed to check purchase:', error);
    }
  }, [token, id]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  useEffect(() => {
    if (project && !highlights) {
      fetchHighlights();
    }
  }, [project, highlights, fetchHighlights]);

  useEffect(() => {
    if (user && project) {
      checkPurchase();
    }
  }, [user, project, checkPurchase]);

  // Fetch relevant suppliers
  const fetchRelevantSuppliers = useCallback(async () => {
    if (relevantSuppliers) return;
    setSuppliersLoading(true);
    try {
      const response = await axios.get(`${API}/projects/${id}/relevant-suppliers`);
      setRelevantSuppliers(response.data);
    } catch (error) {
      console.error('Failed to fetch suppliers:', error);
    }
    setSuppliersLoading(false);
  }, [id, relevantSuppliers]);

  // Fetch suppliers when project loads
  useEffect(() => {
    if (project && !relevantSuppliers) {
      fetchRelevantSuppliers();
    }
  }, [project, relevantSuppliers, fetchRelevantSuppliers]);

  // State for email success modal
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState('');

  // Request PDF via Email - Async delivery
  const handleRequestPDFEmail = async () => {
    if (!token) {
      toast.error("कृपया पहले लॉगिन करें");
      navigate('/auth');
      return;
    }

    setDownloadLoading(true);
    
    try {
      const response = await axios.post(`${API}/projects/${id}/request-pdf`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setEmailSentTo(response.data.email);
        setShowEmailModal(true);
        toast.success("रिक्वेस्ट सफल!");
      }
    } catch (error) {
      console.error('PDF request error:', error);
      toast.error("कुछ गलत हुआ। कृपया दोबारा प्रयास करें।");
    }
    setDownloadLoading(false);
  };

  // Direct Download PDF function (kept for backward compatibility)
  const handleDownloadPDF = async () => {
    if (!token) {
      toast.error("कृपया पहले लॉगिन करें");
      navigate('/auth');
      return;
    }

    setDownloadLoading(true);
    toast.info("PDF जनरेट हो रहा है... कृपया प्रतीक्षा करें");
    
    try {
      const response = await axios.get(`${API}/projects/${id}/download-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
        timeout: 300000 // 5 minute timeout for PDF generation
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `DPR_${project.name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF डाउनलोड हो रहा है!");
    } catch (error) {
      console.error('PDF download error:', error);
      if (error.response?.status === 403) {
        toast.error("PDF डाउनलोड के लिए पहले भुगतान करें");
      } else {
        toast.error("PDF डाउनलोड में त्रुटि हुई। कृपया दोबारा प्रयास करें।");
      }
    }
    setDownloadLoading(false);
  };

  const fetchDPR = async () => {
    if (!token) {
      toast.error("कृपया पहले लॉगिन करें");
      navigate('/auth');
      return;
    }

    setDprLoading(true);
    setShowDPR(true);
    try {
      const response = await axios.get(`${API}/projects/${id}/dpr`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDpr(response.data);
      toast.success("DPR सफलतापूर्वक लोड हो गया");
    } catch (error) {
      toast.error("DPR लोड करने में त्रुटि");
      setShowDPR(false);
    }
    setDprLoading(false);
  };

  const handlePayment = async () => {
    if (!token) {
      toast.error("कृपया पहले लॉगिन करें");
      navigate('/auth');
      return;
    }

    setPaymentLoading(true);
    try {
      const orderResponse = await axios.post(
        `${API}/payments/create-order`,
        { project_id: id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { order_id, amount, key_id } = orderResponse.data;

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        const options = {
          key: key_id,
          amount: amount,
          currency: "INR",
          name: "उद्यमी मित्र",
          description: `${project.name_hindi} - प्रोजेक्ट रिपोर्ट`,
          order_id: order_id,
          handler: async (response) => {
            try {
              await axios.post(
                `${API}/payments/verify`,
                {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  project_id: id
                },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success("भुगतान सफल! अब आप रिपोर्ट डाउनलोड कर सकते हैं");
              setIsPurchased(true);
            } catch (error) {
              toast.error("भुगतान वेरिफिकेशन फेल");
            }
          },
          prefill: {
            name: user?.name,
            email: user?.email,
            contact: user?.phone
          },
          theme: {
            color: "#F97316"
          }
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      };
      document.body.appendChild(script);
    } catch (error) {
      toast.error("भुगतान शुरू करने में त्रुटि");
    }
    setPaymentLoading(false);
  };

  const formatCost = (cost) => {
    return new Intl.NumberFormat('hi-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(cost);
  };

  // WhatsApp Share Functions
  const shareOnWhatsApp = (message) => {
    const encodedMessage = encodeURIComponent(message);
    window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
    toast.success("WhatsApp पर शेयर करें");
  };

  const shareProjectSummary = () => {
    if (!project) return;
    let message = `🏭 *${project.name_hindi}*
(${project.name})

📊 *श्रेणी:* ${project.category}
💰 *अनुमानित लागत:* ${formatCost(project.total_cost)}`;

    if (highlights) {
      message += `
💹 *अनुमानित मुनाफा:* ${highlights.monthly_profit_hindi}
📍 *जगह:* ${highlights.area_required_hindi}
⚡ *बिजली:* ${highlights.power_type_hindi}
⚠️ *जोखिम:* ${highlights.risk_level_hindi}`;
    }

    message += `

🔗 अधिक जानकारी के लिए उद्यमी मित्र पर देखें:
${window.location.href}

---
*उद्यमी मित्र* - अपना उद्योग शुरू करें! 🚀`;
    
    shareOnWhatsApp(message);
  };

  const shareDPRSummary = () => {
    if (!dpr || !dpr.sections) return;
    
    let message = `📋 *${project.name_hindi} - प्रोजेक्ट रिपोर्ट*\n\n`;
    
    const profitSection = dpr.sections.find(s => s.id === 'profitability');
    if (profitSection) {
      message += `💹 *मुनाफा विश्लेषण:*\n`;
      if (profitSection.monthly_sales) message += `• मासिक बिक्री: ${formatCost(profitSection.monthly_sales)}\n`;
      if (profitSection.monthly_profit) message += `• मासिक लाभ: ${formatCost(profitSection.monthly_profit)}\n`;
      if (profitSection.roi_percentage) message += `• ROI: ${profitSection.roi_percentage}%\n\n`;
    }
    
    message += `💰 *कुल लागत:* ${formatCost(project.total_cost)}\n\n`;
    message += `🔗 पूरी रिपोर्ट देखें: ${window.location.href}\n\n`;
    message += `---\n*उद्यमी मित्र* 🚀`;
    
    shareOnWhatsApp(message);
  };

  const shareSuppliers = () => {
    if (!dpr || !dpr.suppliers || dpr.suppliers.length === 0) {
      toast.error("कोई सप्लायर जानकारी उपलब्ध नहीं");
      return;
    }
    
    let message = `📦 *${project.name_hindi} - सप्लायर लिस्ट*\n\n`;
    
    dpr.suppliers.forEach((supplier, index) => {
      message += `${index + 1}. *${supplier.name}*\n`;
      message += `   📍 ${supplier.location}\n`;
      message += `   🏷️ ${supplier.type}\n\n`;
    });
    
    message += `---\n*उद्यमी मित्र* - सप्लायर खोजने में मदद 🤝`;
    
    shareOnWhatsApp(message);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Skeleton className="h-8 w-48 bg-white mb-4" />
          <Skeleton className="h-12 w-96 bg-white mb-8" />
          <Skeleton className="h-64 bg-white" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-6xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-500">प्रोजेक्ट नहीं मिला</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-500 hover:text-orange-500 mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          सभी प्रोजेक्ट्स
        </button>

        {/* Project Header */}
        <div className="bg-gradient-to-br from-white via-gray-50 to-slate-900 rounded-2xl border border-gray-300 p-6 md:p-8 mb-8 shadow-xl">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <Badge className="bg-orange-500/20 text-orange-500 border-orange-500/30 text-sm px-3 py-1">
              {project.category}
            </Badge>
            
            <Button
              onClick={shareProjectSummary}
              className="bg-green-600 hover:bg-green-700 rounded-lg"
              data-testid="whatsapp-share-btn"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              WhatsApp पर शेयर
            </Button>
          </div>
          
          <h1 className="font-rajdhani font-bold text-3xl md:text-4xl text-gray-800 mt-4 mb-2">
            {project.name_hindi}
          </h1>
          <p className="text-gray-8000 text-lg mb-6">{project.name}</p>
          
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <p className="text-sm text-gray-8000 mb-1">अनुमानित लागत</p>
              <p className="font-rajdhani font-bold text-3xl text-orange-500 flex items-center">
                <IndianRupee className="w-6 h-6" />
                {formatCost(project.total_cost)}
              </p>
            </div>
          </div>
        </div>

        {/* Key Metrics Cards */}
        {highlights && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {/* Monthly Profit */}
            <div className="bg-gradient-to-br from-green-500/10 to-slate-900 rounded-xl border border-green-500/30 p-4 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-500/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-8000">अनुमानित मुनाफा</p>
                  <p className="text-lg font-bold text-green-400">{highlights.monthly_profit_hindi}</p>
                </div>
              </div>
            </div>
            
            {/* Area Required */}
            <div className="bg-gradient-to-br from-blue-500/10 to-slate-900 rounded-xl border border-blue-500/30 p-4 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-500/20 rounded-lg">
                  <MapPin className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-8000">जगह चाहिए</p>
                  <p className="text-lg font-bold text-blue-400">{highlights.area_required_hindi}</p>
                </div>
              </div>
            </div>
            
            {/* Power Type */}
            <div className="bg-gradient-to-br from-yellow-500/10 to-slate-900 rounded-xl border border-yellow-500/30 p-4 shadow-lg">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-500/20 rounded-lg">
                  <Zap className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-8000">बिजली</p>
                  <p className="text-lg font-bold text-yellow-400">{highlights.power_type_hindi}</p>
                </div>
              </div>
            </div>
            
            {/* Risk Level */}
            <div className={`bg-gradient-to-br to-slate-900 rounded-xl border p-4 shadow-lg ${getRiskColor(highlights.risk_level_hindi)}`}>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${getRiskColor(highlights.risk_level_hindi).replace('text-', 'bg-').replace('-500', '-500/20')}`}>
                  <AlertTriangle className={`w-5 h-5 ${getRiskColor(highlights.risk_level_hindi).split(' ')[0]}`} />
                </div>
                <div>
                  <p className="text-xs text-gray-8000">जोखिम स्तर</p>
                  <p className={`text-lg font-bold ${getRiskColor(highlights.risk_level_hindi).split(' ')[0]}`}>
                    {highlights.risk_level_hindi}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Project Overview Section (Pre-DPR) */}
        {!showDPR && highlights && (
          <div className="space-y-8 mb-8">
            {/* Introduction Section with Image */}
            <IntroductionSection project={project} highlights={highlights} />
            
            {/* Market Potential Section with Cards */}
            <MarketPotentialSection highlights={highlights} />
            
            {/* Growth Chart */}
            <GrowthChart 
              year1={highlights.growth_year1 || 100}
              year2={highlights.growth_year2 || 140}
              year3={highlights.growth_year3 || 180}
            />

            {/* Relevant Suppliers Section */}
            {relevantSuppliers && (relevantSuppliers.machinery_suppliers?.length > 0 || relevantSuppliers.raw_material_suppliers?.length > 0) && (
              <div className="bg-gradient-to-br from-white via-gray-50 to-slate-900 rounded-2xl border border-gray-300 overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="bg-gradient-to-r from-cyan-600/20 via-cyan-500/10 to-transparent p-6 border-b border-gray-300/50">
                  <h3 className="font-rajdhani font-bold text-2xl text-gray-800 flex items-center gap-3">
                    <div className="p-3 bg-cyan-500/20 rounded-xl border border-cyan-500/30">
                      <Truck className="w-6 h-6 text-cyan-500" />
                    </div>
                    संबंधित सप्लायर
                    <Badge className="ml-2 bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                      {relevantSuppliers.total_suppliers || 0} मिले
                    </Badge>
                  </h3>
                  <p className="text-gray-500 text-sm mt-2 ml-14">
                    इस प्रोजेक्ट के लिए मशीनरी और कच्चा माल सप्लायर
                  </p>
                </div>
                
                <div className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Machinery Suppliers */}
                    {relevantSuppliers.machinery_suppliers?.length > 0 && (
                      <div className="bg-gray-100/50 rounded-xl border border-gray-300 overflow-hidden">
                        <div className="bg-gradient-to-r from-blue-500/20 to-transparent p-4 border-b border-gray-300/50">
                          <h4 className="font-bold text-blue-400 flex items-center gap-2">
                            <Factory className="w-5 h-5" />
                            मशीनरी सप्लायर
                          </h4>
                        </div>
                        <div className="p-4 space-y-3">
                          {relevantSuppliers.machinery_suppliers.map((supplier, idx) => (
                            <div key={idx} className="bg-white/50 rounded-lg p-4 border border-gray-300/50 hover:border-blue-500/30 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h5 className="font-semibold text-gray-700 text-sm">
                                    {supplier.business_name || supplier.name}
                                  </h5>
                                  <p className="text-xs text-gray-8000 mt-1 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {supplier.city || supplier.location || 'भारत'}
                                  </p>
                                  {supplier.products_services && (
                                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                                      {supplier.products_services.substring(0, 80)}...
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col gap-2 ml-3">
                                  {supplier.phone && (
                                    <a href={`tel:${supplier.phone}`} className="p-2 bg-green-500/20 rounded-lg hover:bg-green-500/30 transition-colors">
                                      <Phone className="w-4 h-4 text-green-400" />
                                    </a>
                                  )}
                                  {supplier.source === 'database' && supplier.average_rating > 0 && (
                                    <div className="flex items-center gap-1 text-yellow-400">
                                      <Star className="w-3 h-3 fill-current" />
                                      <span className="text-xs">{supplier.average_rating}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Raw Material Suppliers */}
                    {relevantSuppliers.raw_material_suppliers?.length > 0 && (
                      <div className="bg-gray-100/50 rounded-xl border border-gray-300 overflow-hidden">
                        <div className="bg-gradient-to-r from-green-500/20 to-transparent p-4 border-b border-gray-300/50">
                          <h4 className="font-bold text-green-400 flex items-center gap-2">
                            <Package className="w-5 h-5" />
                            कच्चा माल सप्लायर
                          </h4>
                        </div>
                        <div className="p-4 space-y-3">
                          {relevantSuppliers.raw_material_suppliers.map((supplier, idx) => (
                            <div key={idx} className="bg-white/50 rounded-lg p-4 border border-gray-300/50 hover:border-green-500/30 transition-colors">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h5 className="font-semibold text-gray-700 text-sm">
                                    {supplier.business_name || supplier.name}
                                  </h5>
                                  <p className="text-xs text-gray-8000 mt-1 flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {supplier.city || supplier.location || 'भारत'}
                                  </p>
                                  {supplier.products_services && (
                                    <p className="text-xs text-gray-500 mt-2 line-clamp-2">
                                      {supplier.products_services.substring(0, 80)}...
                                    </p>
                                  )}
                                  {supplier.contact_hint && (
                                    <p className="text-xs text-green-400 mt-2">
                                      💡 {supplier.contact_hint}
                                    </p>
                                  )}
                                </div>
                                <div className="flex flex-col gap-2 ml-3">
                                  {supplier.phone && (
                                    <a href={`tel:${supplier.phone}`} className="p-2 bg-green-500/20 rounded-lg hover:bg-green-500/30 transition-colors">
                                      <Phone className="w-4 h-4 text-green-400" />
                                    </a>
                                  )}
                                  {supplier.source === 'database' && supplier.average_rating > 0 && (
                                    <div className="flex items-center gap-1 text-yellow-400">
                                      <Star className="w-3 h-3 fill-current" />
                                      <span className="text-xs">{supplier.average_rating}</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Supplier Source Info */}
                  <div className="mt-4 p-3 bg-gray-100/30 rounded-lg border border-gray-300/30">
                    <p className="text-xs text-gray-8000 flex items-center gap-2">
                      {relevantSuppliers.source === 'database' ? (
                        <>
                          <BadgeCheck className="w-4 h-4 text-green-400" />
                          ये सप्लायर हमारे डेटाबेस में रजिस्टर्ड हैं
                        </>
                      ) : relevantSuppliers.source === 'mixed' ? (
                        <>
                          <Store className="w-4 h-4 text-cyan-400" />
                          डेटाबेस और ऑनलाइन सर्च से मिले सप्लायर
                        </>
                      ) : (
                        <>
                          <BadgeCheck className="w-4 h-4 text-green-400" />
                          वेरिफाइड सप्लायर जानकारी
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Lead Capture Card - Show when no suppliers found */}
            {relevantSuppliers && relevantSuppliers.show_lead_capture && (
              <div className="bg-gradient-to-br from-orange-500/20 via-white to-slate-900 rounded-2xl border border-orange-500/30 overflow-hidden shadow-2xl">
                <div className="p-8 text-center">
                  <div className="inline-flex p-4 bg-orange-500/20 rounded-full mb-4">
                    <Truck className="w-10 h-10 text-orange-500" />
                  </div>
                  <h3 className="font-rajdhani font-bold text-2xl text-gray-800 mb-3">
                    वेरिफाइड सप्लायर कोटेशन पाएं
                  </h3>
                  <p className="text-gray-500 mb-6 max-w-md mx-auto">
                    इस प्रोजेक्ट के लिए मशीनरी और कच्चे माल के वेरिफाइड सप्लायर से सीधे संपर्क करें। 
                    हमारी टीम 24 घंटे में आपसे संपर्क करेगी।
                  </p>
                  <a 
                    href="/enquiry" 
                    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white font-bold rounded-xl hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg shadow-orange-500/30"
                  >
                    <Phone className="w-5 h-5" />
                    कॉलबैक रिक्वेस्ट करें
                  </a>
                  <p className="text-xs text-gray-8000 mt-4">
                    * कोई शुल्क नहीं - मुफ्त सेवा
                  </p>
                </div>
              </div>
            )}

            {/* Loading state for suppliers */}
            {suppliersLoading && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Loader2 className="w-5 h-5 text-cyan-500 animate-spin" />
                  <p className="text-gray-600">सप्लायर खोज रहे हैं...</p>
                </div>
                <Skeleton className="h-32 bg-gray-100" />
              </div>
            )}
            
            {/* CTA Buttons */}
            <div className="bg-gradient-to-r from-orange-500/20 via-white to-slate-900 rounded-2xl border border-orange-500/30 p-8 shadow-xl">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="font-rajdhani font-bold text-2xl text-gray-800 mb-2 flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-orange-500" />
                    पूरी AI रिपोर्ट देखें
                  </h3>
                  <p className="text-gray-500">
                    विस्तृत जानकारी - मशीनरी लिस्ट, कच्चा माल, लाभ विश्लेषण, टाइमलाइन, सप्लायर
                  </p>
                </div>
                <div className="flex flex-wrap gap-4">
                  <Button
                    onClick={fetchDPR}
                    disabled={dprLoading}
                    className="btn-primary rounded-lg text-lg px-8 py-4 shadow-lg shadow-orange-500/30"
                    data-testid="view-dpr-btn"
                  >
                    {dprLoading ? (
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-5 h-5 mr-2" />
                    )}
                    AI रिपोर्ट बनाएं
                    <ArrowUpRight className="w-4 h-4 ml-2" />
                  </Button>
                  
                  {/* Email Delivery Button - Async PDF Generation */}
                  <Button 
                    onClick={handleRequestPDFEmail}
                    disabled={downloadLoading}
                    className="bg-emerald-600 hover:bg-emerald-700 rounded-lg px-6 py-4" 
                    data-testid="email-pdf-btn"
                  >
                    {downloadLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Mail className="w-4 h-4 mr-2" />
                    )}
                    {downloadLoading ? "प्रोसेसिंग..." : "PDF ईमेल पर भेजें"}
                  </Button>
                </div>
              </div>
            </div>
            
            {highlights.is_estimated && (
              <p className="text-xs text-gray-8000 text-center">
                * ये अनुमानित आंकड़े हैं। AI रिपोर्ट में विस्तृत और सटीक जानकारी मिलेगी।
              </p>
            )}
          </div>
        )}

        {/* DPR Content */}
        {showDPR && (
          <>
            {dprLoading ? (
              <div className="space-y-4">
                <div className="bg-white rounded-xl border border-gray-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                    <p className="text-gray-600">AI रिपोर्ट तैयार हो रही है...</p>
                  </div>
                  <Skeleton className="h-32 bg-gray-100 mb-4" />
                  <Skeleton className="h-32 bg-gray-100 mb-4" />
                  <Skeleton className="h-32 bg-gray-100" />
                </div>
              </div>
            ) : dpr ? (
              <>
                {/* WhatsApp Share Buttons for DPR */}
                <div className="flex flex-wrap gap-3 mb-6">
                  <Button
                    onClick={shareDPRSummary}
                    className="bg-green-600 hover:bg-green-700 rounded-lg"
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    DPR सारांश शेयर करें
                  </Button>
                  <Button
                    onClick={shareSuppliers}
                    variant="outline"
                    className="border-green-600 text-green-500 hover:bg-green-600/10 rounded-lg"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    सप्लायर लिस्ट शेयर करें
                  </Button>
                </div>
                <DPRView dpr={dpr} />
              </>
            ) : null}
          </>
        )}
      </main>

      {/* Email Success Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="bg-white border-gray-300 max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
            </div>
            <DialogTitle className="text-center text-xl text-gray-800">
              सफलतापूर्वक! ✅
            </DialogTitle>
            <DialogDescription className="text-center text-gray-600 mt-4 space-y-3">
              <p className="text-lg">
                आपकी प्रोजेक्ट रिपोर्ट तैयार की जा रही है।
              </p>
              <p className="text-base">
                <span className="text-orange-400 font-semibold">2-3 मिनट</span> के भीतर यह आपके ईमेल पर भेज दी जाएगी:
              </p>
              <p className="text-emerald-400 font-mono bg-gray-100 px-4 py-2 rounded-lg">
                {emailSentTo}
              </p>
              <p className="text-sm text-gray-8000 mt-4">
                कृपया अपना इनबॉक्स और स्पैम फोल्डर चेक करें।
              </p>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button 
              onClick={() => setShowEmailModal(false)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              समझ गया
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
